/**
 * 图片处理服务
 * 负责识别图片节点并获取图片URL
 */

import type { 
  ImageProcessingConfig, 
  ImageProcessingResult, 
  ProcessedImage, 
  FailedImage, 
  ImageProcessingStats,
  ImageNode 
} from '../types/image-processing';
import { FigmaDataFetcher } from '../figma/data-fetcher';
import { Logger } from '../utils/logger';
import { v4 as uuidv4 } from 'uuid';

export class ImageProcessor {
  private config: ImageProcessingConfig;
  private logger: Logger;
  private accessToken: string;
  private fileKey: string;

  constructor(
    fileKey: string,
    accessToken: string,
    config: Partial<ImageProcessingConfig> = {}
  ) {
    this.fileKey = fileKey;
    this.accessToken = accessToken;
    this.config = { ...DEFAULT_IMAGE_CONFIG, ...config };
    this.logger = new Logger({ traceId: uuidv4() });
  }

  /**
   * 处理节点中的图片
   */
  async processImages(nodes: any[]): Promise<ImageProcessingResult> {
    const startTime = Date.now();
    this.logger.info('开始图片处理', { nodeCount: nodes.length });

    const imageNodes = this.identifyImageNodes(nodes);
    this.logger.info(`识别到 ${imageNodes.length} 个图片节点`);

    const processedImages: ProcessedImage[] = [];
    const failedImages: FailedImage[] = [];

    // 批量获取图片URL
    if (imageNodes.length > 0 && this.config.enableImageDownload) {
      const imageHashes = this.extractImageHashes(imageNodes);
      
      if (imageHashes.length > 0) {
        try {
          const imageUrls = await this.fetchImageUrls(imageHashes);
          
          // 处理每个图片节点
          for (const node of imageNodes) {
            try {
              const processedImage = await this.processImageNode(node, imageUrls);
              if (processedImage) {
                processedImages.push(processedImage);
              }
            } catch (error) {
              failedImages.push({
                nodeId: node.id,
                nodeName: node.name || 'unnamed',
                reason: '处理图片节点失败',
                error: error instanceof Error ? error : new Error(String(error))
              });
            }
          }
        } catch (error) {
          this.logger.error('获取图片URL失败', { error });
          failedImages.push(...imageNodes.map(node => ({
            nodeId: node.id,
            nodeName: node.name || 'unnamed',
            reason: '获取图片URL失败',
            error: error instanceof Error ? error : new Error(String(error))
          })));
        }
      }
    }

    const processingTime = Date.now() - startTime;
    const stats: ImageProcessingStats = {
      totalImages: imageNodes.length,
      processedImages: processedImages.length,
      failedImages: failedImages.length,
      totalFileSize: processedImages.reduce((sum, img) => sum + (img.fileSize || 0), 0),
      processingTime
    };

    this.logger.info('图片处理完成', { stats });

    return {
      processedImages,
      failedImages,
      stats
    };
  }

  /**
   * 识别图片节点
   */
  private identifyImageNodes(nodes: any[]): ImageNode[] {
    const imageNodes: ImageNode[] = [];

    const traverse = (nodeList: any[]) => {
      for (const node of nodeList) {
        // 检查是否为图片类型节点
        if (this.isImageNode(node)) {
          imageNodes.push(this.transformToImageNode(node));
        }

        // 检查填充中是否包含图片
        if (node.fills && Array.isArray(node.fills)) {
          const hasImageFill = node.fills.some((fill: any) => 
            fill.type === 'IMAGE' && (fill.imageRef || fill.imageHash)
          );
          
          if (hasImageFill) {
            imageNodes.push(this.transformToImageNode(node));
          }
        }

        // 递归处理子节点
        if (node.children && Array.isArray(node.children)) {
          traverse(node.children);
        }
      }
    };

    traverse(nodes);
    return imageNodes;
  }

  /**
   * 判断是否为图片节点
   */
  private isImageNode(node: any): boolean {
    // 直接图片类型
    if (node.type === 'IMAGE') {
      return true;
    }

    // 包含图片填充的形状
    if (node.fills && Array.isArray(node.fills)) {
      return node.fills.some((fill: any) => fill.type === 'IMAGE');
    }

    // 矢量图形可能包含图片
    const imageEligibleTypes = ['VECTOR', 'RECTANGLE', 'ELLIPSE', 'POLYGON', 'STAR', 'LINE'];
    if (imageEligibleTypes.includes(node.type) && node.fills) {
      return node.fills.some((fill: any) => fill.type === 'IMAGE');
    }

    return false;
  }

  /**
   * 转换为图片节点格式
   */
  private transformToImageNode(node: any): ImageNode {
    const imageFills = node.fills?.filter((fill: any) => fill.type === 'IMAGE') || [];
    
    return {
      id: node.id,
      name: node.name || 'unnamed',
      type: node.type,
      fills: imageFills.map((fill: any) => ({
        type: fill.type,
        imageRef: fill.imageRef,
        imageHash: fill.imageHash,
        scaleMode: fill.scaleMode,
        imageTransform: fill.imageTransform
      })),
      absoluteBoundingBox: node.absoluteBoundingBox
    };
  }

  /**
   * 提取图片节点ID（用于Figma Images API）
   */
  private extractImageHashes(imageNodes: ImageNode[]): string[] {
    // Figma Images API需要的是节点ID，不是imageRef
    const nodeIds: string[] = [];
    
    for (const node of imageNodes) {
      if (node.fills) {
        for (const fill of node.fills) {
          if (fill.imageRef || fill.imageHash) {
            // 推送节点ID而不是imageRef
            nodeIds.push(node.id);
          }
        }
      }
    }
    
    return [...new Set(nodeIds)]; // 去重
  }

  /**
   * 获取图片URL
   */
  private async fetchImageUrls(imageHashes: string[]): Promise<Record<string, string>> {
    this.logger.info('获取图片URL', { hashCount: imageHashes.length });

    try {
      // 使用工具函数获取图片URL
      const imageUrls = await fetchFigmaImages(
        this.accessToken,
        this.fileKey,
        imageHashes,
        {
          format: this.config.format,
          scale: this.config.scale
        }
      );

      this.logger.info(`成功获取 ${Object.keys(imageUrls).length} 个图片URL`);
      return imageUrls;
    } catch (error) {
      this.logger.error('获取图片URL失败', { error });
      throw error;
    }
  }

  /**
   * 处理单个图片节点
   */
  private async processImageNode(node: ImageNode, imageUrls: Record<string, string>): Promise<ProcessedImage | null> {
    if (!node.fills || node.fills.length === 0) {
      return null;
    }

    // 获取第一个有效的图片填充
    const imageFill = node.fills.find(fill => fill.imageRef || fill.imageHash);
    if (!imageFill) {
      return null;
    }

    const imageHash = imageFill.imageRef || imageFill.imageHash!;
    // 现在imageUrls的键是节点ID，不是imageRef
    const imageUrl = imageUrls[node.id];

    if (!imageUrl) {
      throw new Error(`未找到图片URL，节点ID: ${node.id}, imageHash: ${imageHash}`);
    }

    // 获取图片信息
    let fileSize = 0;
    let format: 'png' | 'jpg' | 'svg' = this.config.format;

    try {
      if (this.config.enableImageDownload) {
        const imageInfo = await this.getImageInfo(imageUrl);
        fileSize = imageInfo.fileSize;
        format = imageInfo.format;
      }
    } catch (error) {
      this.logger.warn('获取图片信息失败', { imageUrl, error });
    }

    return {
      nodeId: node.id,
      nodeName: node.name,
      imageUrl,
      imageHash,
      dimensions: {
        width: node.absoluteBoundingBox?.width || 0,
        height: node.absoluteBoundingBox?.height || 0
      },
      format,
      fileSize
    };
  }

  /**
   * 获取图片信息
   */
  private async getImageInfo(imageUrl: string): Promise<{ fileSize: number; format: 'png' | 'jpg' | 'svg' }> {
    try {
      const response = await fetch(imageUrl, { method: 'HEAD' });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const contentLength = response.headers.get('content-length');
      const contentType = response.headers.get('content-type');
      
      const fileSize = contentLength ? parseInt(contentLength) : 0;
      let format: 'png' | 'jpg' | 'svg' = this.config.format;

      if (contentType) {
        if (contentType.includes('svg')) format = 'svg';
        else if (contentType.includes('jpeg') || contentType.includes('jpg')) format = 'jpg';
        else if (contentType.includes('png')) format = 'png';
      }

      return { fileSize, format };
    } catch (error) {
      this.logger.error('获取图片信息失败', { imageUrl, error });
      return { fileSize: 0, format: this.config.format };
    }
  }

  /**
   * 将图片信息拼接回Figma JSON
   */
  appendImageInfoToNodes(nodes: any[], processedImages: ProcessedImage[]): any[] {
    const imageMap = new Map(processedImages.map(img => [img.nodeId, img]));

    const appendToNode = (node: any): any => {
      const processedImage = imageMap.get(node.id);
      
      if (processedImage) {
        // 在节点中添加图片信息
        node.imageInfo = {
          url: processedImage.imageUrl,
          hash: processedImage.imageHash,
          format: processedImage.format,
          fileSize: processedImage.fileSize,
          dimensions: processedImage.dimensions
        };

        // 更新填充信息
        if (node.fills && Array.isArray(node.fills)) {
          node.fills = node.fills.map((fill: any) => {
            if (fill.type === 'IMAGE' && (fill.imageRef === processedImage.imageHash || fill.imageHash === processedImage.imageHash)) {
              return {
                ...fill,
                url: processedImage.imageUrl,
                format: processedImage.format
              };
            }
            return fill;
          });
        }
      }

      // 递归处理子节点
      if (node.children && Array.isArray(node.children)) {
        node.children = node.children.map(appendToNode);
      }

      return node;
    };

    return nodes.map(appendToNode);
  }
}

/**
 * 获取Figma图片URL的工具函数
 */
async function fetchFigmaImages(
  accessToken: string,
  fileKey: string,
  imageHashes: string[],
  options: {
    format?: 'png' | 'jpg' | 'svg';
    scale?: number;
  } = {}
): Promise<Record<string, string>> {
  const format = options.format || 'png';
  const scale = options.scale || 1;
  
  const params = new URLSearchParams({
    format,
    scale: scale.toString(),
    ids: imageHashes.join(',')
  });

  const url = `https://api.figma.com/v1/images/${fileKey}?${params.toString()}`;
  
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'X-Figma-Token': accessToken,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Figma API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.images || {};
  } catch (error) {
    throw new Error(`获取图片失败: ${error}`);
  }
}

// 默认配置常量
const DEFAULT_IMAGE_CONFIG: ImageProcessingConfig = {
  enableImageDetection: true,
  enableImageDownload: true,
  imageFormats: ['png', 'jpg', 'svg'],
  maxImageSize: 5 * 1024 * 1024, // 5MB
  scale: 2,
  format: 'png'
};